import type { AdminMoneyHealthDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AllClearIcon,
  BlindSpotIcon,
  ClosingIcon,
  EscalatedIcon,
  PayoutFailIcon,
  WebhookIcon,
} from './icons';
import { SectionCard } from './SectionCard';
import { SectionMeta } from './SectionMeta';
import { tokens } from '../../../design';
import { scanText, thresholdText } from '../_lib/moneyHealthText';

const CheckRow = ({
  icon,
  name,
  what,
  testId,
}: {
  icon: ReactNode;
  name: string;
  what: string;
  testId: string;
}): ReactElement => (
  <Box
    data-testid={testId}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2.5,
      px: 3.5,
      py: 1.75,
      borderBottom: `1px solid ${tokens.border.default}`,
      '&:last-of-type': { borderBottom: 0 },
    }}
  >
    <Box
      aria-hidden
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        background: tokens.surface.muted,
        color: tokens.text.tertiary,
        '& svg': { width: 14, height: 14 },
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}>{name}</Typography>
    <Typography sx={{ fontSize: 11.5, color: tokens.text.tertiary, minWidth: 0 }}>
      {what}
    </Typography>
    <Typography
      sx={{
        ml: 'auto',
        fontFamily: tokens.font.mono,
        fontSize: 14,
        fontWeight: 700,
        color: tokens.state.success,
      }}
    >
      0
    </Typography>
  </Box>
);

/** The affirmative statement itself, plus when it was checked. */
const AllClearBanner = ({
  refreshSeconds,
  checkedAt,
}: {
  refreshSeconds: number;
  checkedAt: string;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="all-clear"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        px: 5,
        py: 4,
        background: tokens.surface.base,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: `${tokens.radius.card}px`,
        boxShadow: tokens.shadow.card,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '12px',
          background: tokens.state.successBg,
          color: tokens.state.success,
          '& svg': { width: 24, height: 24 },
        }}
      >
        <AllClearIcon />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700 }}>
          {t('moneyHealth.allClear.title')}
        </Typography>
        <Typography sx={{ fontSize: 12.5, lineHeight: '18px', color: tokens.text.secondary }}>
          {t('moneyHealth.allClear.body')}
        </Typography>
      </Box>
      <Box sx={{ ml: 'auto', flexShrink: 0, textAlign: 'right' }}>
        <Typography sx={{ fontSize: 10, color: tokens.text.tertiary }}>
          {t('moneyHealth.allClear.stampLabel')}
        </Typography>
        <Typography
          sx={{ fontFamily: tokens.font.mono, fontSize: 15, fontWeight: 700 }}
          data-testid="all-clear-stamp"
        >
          {checkedAt}
        </Typography>
        <Typography sx={{ fontSize: 10, color: tokens.text.tertiary }}>
          {t('moneyHealth.allClear.stampSub', { seconds: refreshSeconds })}
        </Typography>
      </Box>
    </Box>
  );
};

/** «Что проверено» — four named checks, so "пусто" cannot read as "сломалось". */
const ChecksCard = ({ data }: { data: AdminMoneyHealthDto }): ReactElement => {
  const { t } = useTranslation();
  const closingThreshold = thresholdText(data.thresholds.holdClosingLeadMs);
  const webhookThreshold = thresholdText(data.thresholds.webhookStaleAfterMs);
  return (
    <SectionCard
      data-testid="all-clear-checks"
      icon={<AllClearIcon />}
      title={t('moneyHealth.allClear.checksTitle')}
      subtitle={t('moneyHealth.allClear.checksHint')}
      count=""
      footer={
        <>
          <SectionMeta text={{ key: 'moneyHealth.allClear.noTruncation' }} />
          <SectionMeta text={scanText(data.holdScan)} icon={<BlindSpotIcon />} />
        </>
      }
    >
      <CheckRow
        testId="check-escalated"
        icon={<EscalatedIcon />}
        name={t('moneyHealth.signals.escalated.label')}
        what={t('moneyHealth.allClear.checks.escalated')}
      />
      <CheckRow
        testId="check-closing"
        icon={<ClosingIcon />}
        name={t('moneyHealth.signals.closing.label')}
        what={t('moneyHealth.allClear.checks.closing', {
          threshold: t(closingThreshold.key, { ...closingThreshold.values }),
        })}
      />
      <CheckRow
        testId="check-payouts"
        icon={<PayoutFailIcon />}
        name={t('moneyHealth.signals.payouts.label')}
        what={t('moneyHealth.allClear.checks.payouts')}
      />
      <CheckRow
        testId="check-webhooks"
        icon={<WebhookIcon />}
        name={t('moneyHealth.signals.webhooks.label')}
        what={t('moneyHealth.allClear.checks.webhooks', {
          threshold: t(webhookThreshold.key, { ...webhookThreshold.values }),
        })}
      />
    </SectionCard>
  );
};

/**
 * The healthy day: ONE affirmative statement, not four «Нет данных» cards.
 *
 * Four empty cards read as "the screen is broken"; a sentence that says the
 * four checks ran and found nothing reads as what it is. The «Что проверено»
 * list underneath exists for the same reason — «пусто» and «не загрузилось»
 * must never look alike on a screen whose whole job is noticing.
 *
 * The scan footnote is rendered here too: even on a clean screen the operator
 * gets to know whether the counters were exact.
 */
export const AllClearPanel = ({
  data,
  refreshSeconds,
  checkedAt,
}: {
  data: AdminMoneyHealthDto;
  refreshSeconds: number;
  /** `computedAt`, already formatted — the stamp never re-derives a clock. */
  checkedAt: string;
}): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
    <AllClearBanner refreshSeconds={refreshSeconds} checkedAt={checkedAt} />
    <ChecksCard data={data} />
  </Box>
);
